import * as THREE from 'three';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';

import { createNeutralLightingMaterial } from '../../utils/glbUtils';
import { buildShapeWithHoles } from './cadUtils';
import { CadPolygon } from './types';

/**
 * Builds unique mesh names from polygon names.
 * Ensures GLB node names are unique for correct layer correlation.
 * If duplicates exist, appends _1, _2, etc.
 */
function buildUniqueMeshNames(polygons: CadPolygon[]): string[] {
  const result: string[] = [];
  const used = new Set<string>();

  for (const p of polygons) {
    let name = p.name?.trim() || 'Polygon';

    if (used.has(name)) {
      let suffix = 1;
      while (used.has(`${name}_${suffix}`)) suffix++;
      name = `${name}_${suffix}`;
    }

    used.add(name);
    result.push(name);
  }

  return result;
}

/**
 * Generates a GLB binary blob from an array of CadPolygons.
 *
 * Each polygon becomes a named mesh (using polygon.name) for layer identification.
 * All transforms are applied (baked) into mesh geometry before export,
 * equivalent to Blender's "Apply All Transforms" (Ctrl+A).
 * Geometry is centered at the origin.
 *
 * @returns blob  – the GLB binary
 * @returns meshNames – the unique mesh name used for each polygon (same order)
 */
export async function generateProjectGlb(
  polygons: CadPolygon[],
  options: { recenter?: boolean } = {},
): Promise<{ blob: Blob; meshNames: string[] }> {
  const { recenter = true } = options;
  const scene = new THREE.Scene();
  scene.name = 'CAD_Scene';

  const meshNames = buildUniqueMeshNames(polygons);

  // Single shared material for every primitive in the export. Layer-level
  // colour metadata travels through the backend `meshes` payload separately,
  // so the in-GLB material is deliberately uniform and tuned for downstream
  // lighting visualisation (see `createNeutralLightingMaterial`).
  const sharedMaterial = createNeutralLightingMaterial();

  polygons.forEach((polygon, index) => {
    if (polygon.points.length < 3) return;

    const shape = buildShapeWithHoles(polygon.points, polygon.holes);

    const height = polygon.height || 0.1;
    const geometry = new THREE.ExtrudeGeometry(shape, {
      depth: height,
      bevelEnabled: false,
    });
    geometry.rotateX(-Math.PI / 2); // XY plane -> XZ plane (Y-up convention)

    const mesh = new THREE.Mesh(geometry, sharedMaterial);
    mesh.name = meshNames[index];
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    scene.add(mesh);
  });

  // Center geometry on X/Z (footprint centered at origin) and
  // floor-align on Y (bottom face at y=0 so the mesh sits on z=0 in workspace).
  // Previously used center.y which pushed the floor to y=-height/2.
  const box = new THREE.Box3().setFromObject(scene);
  if (!box.isEmpty()) {
    const center = box.getCenter(new THREE.Vector3());
    scene.traverse((obj) => {
      if (obj instanceof THREE.Mesh && obj.geometry) {
        // X/Z recentering is opt-in: when an external reference frame (e.g. the
        // uploaded plan image) defines the origin, callers pass `recenter: false`
        // so the polygons keep their world position relative to that frame.
        const tx = recenter ? -center.x : 0;
        const tz = recenter ? -center.z : 0;
        obj.geometry.translate(tx, -box.min.y, tz);
      }
    });
  }

  // ── Apply all transforms (bake into geometry) ────────────────────────
  // Equivalent to Blender's Ctrl+A → "Apply All Transforms".
  // Ensures every mesh node in the GLB carries an identity TRS
  // with all rotations / translations baked into vertex positions & normals.
  scene.updateMatrixWorld(true);
  scene.traverse((obj) => {
    if (obj instanceof THREE.Mesh && obj.geometry) {
      obj.geometry.applyMatrix4(obj.matrixWorld);
      obj.geometry.computeVertexNormals();
      obj.position.set(0, 0, 0);
      obj.rotation.set(0, 0, 0);
      obj.scale.set(1, 1, 1);
      obj.updateMatrix();
    }
  });

  const exporter = new GLTFExporter();
  const glb = await exporter.parseAsync(scene, { binary: true });

  // Dispose geometry; the material is shared so dispose it once at the end.
  scene.traverse((obj) => {
    const m = obj as THREE.Mesh;
    m.geometry?.dispose();
  });
  sharedMaterial.dispose();

  return {
    blob: new Blob([glb as ArrayBuffer], { type: 'model/gltf-binary' }),
    meshNames,
  };
}
